import { useEffect, useMemo, useRef } from 'react'
import { useFormikContext } from 'formik'

type Props = {
  allowedOwnerIds: Set<string> | null
  currentUserId?: string
  ownerError?: string
  keepOwnerId?: string
}

export function OwnerScopeGuard(props: Props) {
  const { allowedOwnerIds, currentUserId, ownerError, keepOwnerId } = props
  const { values, setFieldValue, validateForm } = useFormikContext<{
    ownerId?: string
  }>()
  const ownerId = values.ownerId

  const canonicalIds = useMemo(
    () =>
      allowedOwnerIds
        ? new Map([...allowedOwnerIds].map((id) => [id.toLowerCase(), id]))
        : null,
    [allowedOwnerIds],
  )

  useEffect(() => {
    if (!canonicalIds || !ownerId) return
    if (ownerId === 'unassigned' || ownerId === '-1') return
    if (keepOwnerId && ownerId.toLowerCase() === keepOwnerId.toLowerCase())
      return
    const match = canonicalIds.get(ownerId.toLowerCase())
    if (match === ownerId) return
    const fallback = currentUserId
      ? canonicalIds.get(currentUserId.toLowerCase())
      : undefined
    setFieldValue('ownerId', match ?? fallback ?? '')
  }, [canonicalIds, ownerId, currentUserId, keepOwnerId, setFieldValue])

  const lastOwnerError = useRef(ownerError)
  useEffect(() => {
    if (lastOwnerError.current === ownerError) return
    lastOwnerError.current = ownerError
    validateForm()
  }, [ownerError, validateForm])

  return null
}
